import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslatePipe } from '@ngx-translate/core';

export interface ConfirmDialogData {
  title: string;
  prompt: string;
  /**
   * Demand this exact text be typed out before confirming becomes possible. For an action destructive
   * enough that a skimmed prompt and a reflex click is a real risk — the phrase is normally the name of
   * the thing being destroyed, so typing it out means having read *which* one it is.
   *
   * Leave unset for ordinary yes/no confirmations, which are unaffected.
   */
  requirePhrase?: string;
}

/**
 * The app's yes/no confirmation. Reached through {@link ConfirmationService}, never opened directly.
 *
 * Optionally gated on typing out a phrase — see {@link ConfirmDialogData.requirePhrase}.
 */
@Component({
  selector: 'app-confirm-dialog',
  templateUrl: './confirm-dialog.component.html',
  imports: [MatDialogModule, MatButtonModule, MatFormFieldModule, MatInputModule, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmDialogComponent {
  readonly data = inject<ConfirmDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<ConfirmDialogComponent, boolean>);

  /** The phrase to demand, or null when this is an ordinary confirmation. */
  protected readonly requiredPhrase = this.data.requirePhrase?.trim() || null;

  protected readonly typed = signal('');

  /**
   * Whether confirming is allowed. Always true without a required phrase, so ordinary confirmations keep
   * behaving exactly as they did.
   */
  protected readonly canConfirm = computed(
    () => this.requiredPhrase === null || this.typed().trim() === this.requiredPhrase,
  );

  /** Say so once they have typed something that isn't it — but stay quiet while the field is empty. */
  protected readonly showMismatch = computed(() => this.typed().trim().length > 0 && !this.canConfirm());

  protected onInput(event: Event): void {
    this.typed.set((event.target as HTMLInputElement).value);
  }

  /** Bound to the confirm button and to Enter in the phrase field. */
  protected confirm(): void {
    if (this.canConfirm()) this.dialogRef.close(true);
  }
}
